/**
 * Station Photo URL Helper (sw-ev-user)
 *
 * Frontend fallback for building a resolvable URL from a stored photo
 * reference. The API is the primary source of truth — every station
 * response now includes `verified_data.photo_url` derived server-side
 * via `stationImageUrlForKey`. Callers SHOULD prefer that value; this
 * helper exists for the transition window where an older cached
 * response, an intermediate state, or a legacy code path hands us
 * just a `photo_name`.
 *
 * Shape resolution (first match wins):
 *
 *   1. Absolute http(s) URL — pass through unchanged. Belt-and-braces
 *      for callers that already got a resolved URL from the API and
 *      routed it through this helper by mistake.
 *
 *   2. Canonical GCS object key — starts with `stations/` and matches
 *      the `stations/{id}/photos/{ts}-{hex}.webp` shape. Route through
 *      the configured public-media base URL
 *      (`VITE_STATION_MEDIA_PUBLIC_BASE_URL`), or fall back to the raw
 *      GCS host + bucket when only `VITE_STATION_MEDIA_BUCKET` is set.
 *      Never URL-encode the slashes — that was the bug behind the
 *      /static/stations/stations%2F… render on the user profile.
 *
 *   3. Legacy on-disk filename — no path separator, matches the
 *      pre-GCS naming convention. Serve via the API's static mount
 *      at `/static/stations/<filename>`. `encodeURIComponent` is safe
 *      here because a legacy filename by definition contains no
 *      slashes.
 *
 *   4. Anything else — log a warning and return null so the caller
 *      renders a placeholder instead of a broken image.
 */

// Canonical station-photo GCS key. Mirrors the server-side KEY_RE in
// sw-ev-api services/stationMedia/stationMediaStorage.js and the
// admin-side regex in sw-ev-admin src/utils/stationPhotos.js so all
// three surfaces agree on what counts as an app-owned reference.
const CANONICAL_KEY_RE = /^stations\/[^/]+\/photos\/\d+-[0-9a-f]{8}\.webp$/;

// Pre-GCS legacy filename shape. Mirrors LEGACY_FILENAME_RE server-side.
const LEGACY_FILENAME_RE = /^[A-Za-z0-9_-]+-\d+-[0-9a-f]{4,}\.[a-zA-Z0-9]+$/;

/**
 * Resolve a station photo reference into an absolute URL. Prefer the
 * API-provided `photo_url` on station responses; use this helper only
 * when only a `photo_name` is available (or as belt-and-braces in a
 * layer that doesn't know which was returned).
 *
 * @param {string|null|undefined} photoName - Value from the API.
 * @returns {string|null} Absolute URL, or null when unresolvable.
 */
export function getStationPhotoUrl(photoName) {
  if (!photoName || typeof photoName !== 'string') return null;

  // 1) Absolute URL — pass through.
  if (/^https?:\/\//i.test(photoName)) return photoName;

  // 2) Canonical GCS object key.
  if (CANONICAL_KEY_RE.test(photoName)) {
    const publicBase = (import.meta.env.VITE_STATION_MEDIA_PUBLIC_BASE_URL || '')
      .trim()
      .replace(/\/+$/, '');
    if (publicBase) {
      return `${publicBase}/${photoName}`;
    }
    const bucket = (import.meta.env.VITE_STATION_MEDIA_BUCKET || '').trim();
    if (bucket) {
      return `https://storage.googleapis.com/${bucket}/${photoName}`;
    }
    // Neither env is set — the API is the authoritative source. This
    // branch is a diagnostic, not a common path.
    console.warn(
      '[StationPhoto] Canonical GCS key received without an API-provided photo_url. '
      + 'Set VITE_STATION_MEDIA_PUBLIC_BASE_URL (preferred) or VITE_STATION_MEDIA_BUCKET '
      + 'to enable frontend URL fallback.',
      { photoName },
    );
    return null;
  }

  // 3) Legacy on-disk filename — serve via /static/stations/.
  if (LEGACY_FILENAME_RE.test(photoName)) {
    const apiBase = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || '';
    if (!apiBase) {
      console.warn('[StationPhoto] No API base URL configured for legacy static path');
      return null;
    }
    const origin = apiBase.replace(/\/api\/?$/, '');
    return `${origin}/static/stations/${encodeURIComponent(photoName)}`;
  }

  // 4) Unrecognised shape — do not fabricate a URL.
  console.warn(
    '[StationPhoto] Unrecognised photo reference shape; returning null so the placeholder renders',
    { photoName },
  );
  return null;
}

/**
 * Gets a station photo URL with cache-busting parameter.
 *
 * Canonical GCS keys are immutable — every replacement mints a fresh
 * key ({ts}-{hex}), so appending `?t=<now>` is redundant AND actively
 * harmful because it forces the CDN to bypass its cache on every load
 * (which was the whole point of the immutable-key design).
 *
 * We therefore only append cache-bust for LEGACY filenames — those
 * are the mutable ones served via /static/stations/, where the same
 * filename could point at different bytes after a same-name reupload.
 * Canonical keys and absolute URLs pass through unchanged.
 *
 * @param {string|null} photoName - The photo reference.
 * @param {number} [cacheBust] - Timestamp for cache-busting.
 * @returns {string|null} URL, cache-busted only for mutable legacy names.
 */
export function getStationPhotoUrlWithCacheBust(photoName, cacheBust = Date.now()) {
  const url = getStationPhotoUrl(photoName);
  if (!url) return null;

  // Only cache-bust when the underlying resource is mutable at the
  // same address. Immutable canonical keys and absolute (backend-
  // supplied, potentially CDN-fronted) URLs are left alone.
  const isImmutableAddress =
    typeof photoName === 'string'
    && (/^https?:\/\//i.test(photoName) || CANONICAL_KEY_RE.test(photoName));
  if (isImmutableAddress) return url;

  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}t=${cacheBust}`;
}

/**
 * Given a station-shaped object from the API, resolve the canonical
 * URL to render. Prefers API-provided fields; falls back to helper
 * resolution on the raw reference.
 *
 * Accepts either snake_case (`photo_url` / `photo_name`) or camelCase
 * (`photoUrl` / `photoName`) so it works uniformly whether called
 * against a raw API response, a flattened station object, or a
 * shaped session-context payload.
 *
 * @param {object|null|undefined} station
 * @returns {string|null}
 */
export function resolveStationPhotoUrl(station) {
  if (!station || typeof station !== 'object') return null;
  // Priority: API-provided URL wins over any client-side resolution.
  const provided = station.photoUrl ?? station.photo_url;
  if (typeof provided === 'string' && provided.length > 0) return provided;
  const name = station.photoName ?? station.photo_name;
  return getStationPhotoUrl(name);
}
